import { useReactiveVar } from '@apollo/client';
import { Button, Skeleton, Stack, Typography } from '@mui/material';
import { ReactNode, useCallback, useMemo } from 'react';

import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { newHypotheticalItemNotificationVar } from '@/graphql/reactiveVars';
import { useReportError } from '@/hooks/useReportError';
import {
  GrowthProfilesTrowser,
  GrowthProfilesTrowserScope,
  growthProfilesTrowserTarget,
} from '@/modules/growthProfiles/GrowthProfilesTrowser/GrowthProfilesTrowser';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { getNodes } from '@/utils/graphqlUtils';

import {
  dismissNewHypotheticalItemNotification,
  undoDismissNewHypotheticalItemNotification,
} from '../../EstateWaterfall.utils';
import { useEntityNameQuery } from './graphql/EntityName.generated';
import { useProposalNameQuery } from './graphql/ProposalName.generated';

interface HypotheticalGratNotificationCalloutProps {
  entityId?: string;
  proposalId?: string;
}

export function HypotheticalGratNotificationCallout({
  entityId,
  proposalId,
}: HypotheticalGratNotificationCalloutProps) {
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();

  const notification = useReactiveVar(newHypotheticalItemNotificationVar);
  const growthProfilesTrowser = useTrowser(TrowserKey.GrowthProfiles);
  // This callout renders for an entity or a proposal, and both can be on screen
  // at once, so that id identifies which callout's trowser is open.
  const trowserTarget = growthProfilesTrowserTarget(
    GrowthProfilesTrowserScope.HypotheticalGratCallout,
    entityId ?? proposalId
  );

  const {
    data: entityData,
    loading: entityLoading,
    error: entityError,
  } = useEntityNameQuery({
    variables: {
      id: entityId ?? '',
    },
    onError: (error) => {
      reportError('Failed to load entity details', error);
    },
    skip: !entityId,
  });

  const {
    data: proposalData,
    loading: proposalLoading,
    error: proposalError,
  } = useProposalNameQuery({
    variables: {
      id: proposalId ?? '',
    },
    onError: (error) => {
      reportError('Failed to load proposal details', error);
    },
    skip: !proposalId,
  });

  const householdId = useMemo(() => {
    const entity = getNodes(entityData?.entities)[0];
    const proposal = getNodes(proposalData?.proposals)[0];
    return entity?.household?.id || proposal?.household?.id;
  }, [entityData, proposalData]);

  const handleViewGrowthProfiles = useCallback(() => {
    if (!householdId) {
      reportError(
        'Failed to navigate to growth profiles: No household ID found'
      );
      return;
    }
    growthProfilesTrowser.open({ target: trowserTarget });
  }, [growthProfilesTrowser, householdId, reportError, trowserTarget]);

  const handleDismiss = useCallback(() => {
    dismissNewHypotheticalItemNotification();
    showFeedback('Successfully dismissed', {
      variant: 'success',
      alertActionButton: {
        label: 'Undo',
        buttonProps: {
          onClick: () => {
            undoDismissNewHypotheticalItemNotification();
          },
        },
      },
    });
  }, [showFeedback]);

  const entityName: string | ReactNode = useMemo(() => {
    if (entityLoading || proposalLoading) {
      return (
        <Skeleton
          sx={{
            display: 'inline-block',
          }}
          variant="text"
          width={100}
        />
      );
    }

    if (!entityData && !proposalData) {
      return '';
    }

    const entity = getNodes(entityData?.entities)[0];
    const proposal = getNodes(proposalData?.proposals)[0];
    const displayName = entity?.subtype?.displayName || proposal?.displayName;
    return displayName ?? '';
  }, [entityData, proposalData, entityLoading, proposalLoading]);

  // If there's no notification or it has been dismissed, don't show the callout
  if (
    !notification ||
    notification.dismissed ||
    entityError ||
    proposalError ||
    ((entityData || proposalData) && !entityName)
  ) {
    return null;
  }

  // If there was an error loading the entity details, don't show the callout
  if (entityError || proposalError) {
    return null;
  }

  return (
    <>
      <Callout severity="info-high">
        <Stack spacing={1}>
          <Typography variant="body-xs">
            Add entity-specific growth or future valuation information to &quot;
            {entityName}&quot; by creating a new growth profile or updating an
            existing one.
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              onClick={handleViewGrowthProfiles}
              data-testid="hypothetical-grat-notification-view-button"
            >
              View growth profiles
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={handleDismiss}
              data-testid="hypothetical-grat-notification-dismiss-button"
            >
              Dismiss
            </Button>
          </Stack>
        </Stack>
      </Callout>
      {growthProfilesTrowser.target === trowserTarget && householdId && (
        <GrowthProfilesTrowser
          onClose={growthProfilesTrowser.close}
          householdId={householdId}
        />
      )}
    </>
  );
}
